import { NetworkStatus, useQuery } from "@apollo/client";
import { getOrderDataOpts, getOrderDataQuery, getOrderItemsQuery, OrderData, OrderDataResponse } from "./getOrderData";

type FetchOrderResponse = ({
  status: "loading"
} | {
  status: "error",
  error: Error
} | {
  status: "ready",
  orderData: OrderDataResponse["order"]
}) & {
  refetchOrder: () => Promise<void>
}

export function useFetchOrder(orderId: string): FetchOrderResponse {
  const orderQuery = useQuery(getOrderDataQuery, getOrderDataOpts(orderId));
  const refetchOrder = async () => {
    await orderQuery.refetch();
  };

  if (orderQuery.networkStatus === NetworkStatus.loading) {
    return { status: "loading", refetchOrder };
  }
  if (orderQuery.error) {
    return { status: "error", error: orderQuery.error, refetchOrder };
  }

  const orderData = orderQuery.data.order;
  if (!orderData){
    return { status: "ready", orderData: null, refetchOrder };
  }

  const hasNextPage = orderData.lineItems.pageInfo.hasNextPage;

  if (hasNextPage) {
    const cursor = () => {
      if (!orderQuery.data || !orderQuery.data.order) return undefined;
      const items = orderQuery.data.order.lineItems.edges;
      if (!items.length) return undefined;
      return items[items.length - 1].cursor;
    };

    orderQuery.fetchMore({
      ...getOrderDataOpts(orderId, cursor()),
      query: getOrderItemsQuery,
      updateQuery: (prevResult, { fetchMoreResult }) => {
        return mergeOrderResults(prevResult, fetchMoreResult);
      }
    })
      .catch(err => console.error(err));

    return { status: "loading", refetchOrder };
  } else {
    return { status: "ready", orderData: orderQuery.data.order, refetchOrder };
  }
}

/**
 * Merge previous and current results of paginated `getOrderData` query:
 * - concat `lineItems` nodes
 * - set `lineItems` `cursor` & `hasNextPage` to current values
 *
 * @param prev - previous `getOrderData` query result
 * @param curr - current `getOrderData` query result
 * @return {{order: *}}
 */
function mergeOrderResults(prev: OrderDataResponse, curr: OrderDataResponse): OrderDataResponse {
  const order: OrderData = {
    ...prev.order,
    lineItems: {
      ...curr.order.lineItems,
      edges: mergeItems(prev.order.lineItems.edges, curr.order.lineItems.edges)
    }
  };

  return { order };
}

type LineItemEdge = OrderData['lineItems']['edges'][number];

/**
 * Concatenate two arrays of `LineItemEdge` objects and ensure no
 * duplicates are in result by `node.id` property
 * In case of duplicate items, the curr one is used
 * @param prev - array with previously fetched items
 * @param curr - array with the currently fetched page of items
 */
const mergeItems = (prev: LineItemEdge[], curr: LineItemEdge[]) => {
  const map = new Map<string, LineItemEdge>();
  prev.forEach(item => map.set(item.node.id, item));
  curr.forEach(item => map.set(item.node.id, item));
  return Array.from(map.values());
}